import * as React from 'react';

import PropTypes from 'prop-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import { AUTH_METHODS_NO_DISABLED_BUTTON } from 'bundles/authentication/shared/components/third-party-auth/constants';
import type { THIRD_PARTY_AUTH_METHODS } from 'bundles/authentication/shared/components/third-party-auth/constants';
import thirdPartyAuth from 'bundles/third-party-auth/lib';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

type WrappedComponentProps = {
  disabled: boolean;
  onSuccess: (response?: unknown) => void;
  showRedesign?: boolean;
  onClick?: (response?: unknown) => void;
};

type WrappedComponentType = React.SFC<WrappedComponentProps>;

type Props = {
  onSuccess: (response?: unknown) => void;
  showRedesign?: boolean;
};

type State = {
  disabled: boolean;
};

type Context = {
  router: InjectedRouter;
};

type HOCType = (
  WrappedComponent: WrappedComponentType,
  authControlsType: THIRD_PARTY_AUTH_METHODS
) => React.ComponentClass<Props, State>;

const withSocialAuthControls: HOCType = (WrappedComponent, authControlsType) => {
  class SocialAuthControlsHOC extends React.Component<Props, State, Context> {
    state = {
      disabled: false,
    };

    static contextTypes = {
      router: PropTypes.object.isRequired,
    };

    disableButton(disabled: boolean, callback: () => void) {
      // Do not disable certain buttons—as of August 24 2020, we need to not disable Facebook because otherwise users can't
      // click on that button to log in with their Facebook account
      if (
        AUTH_METHODS_NO_DISABLED_BUTTON.find((authMethodNoDisabled) => authMethodNoDisabled === authControlsType) !==
        undefined
      ) {
        callback();
      } else {
        this.setState(() => ({ disabled }), callback);
      }
    }

    onSuccessCallback = (response: Record<string, unknown>) => {
      const { onSuccess } = this.props;

      Instrumentation.thirdPartyAuth(authControlsType, response);
      onSuccess(response?.isRegistration);
    };

    handleThirdPartyAuthSuccess = (response: Record<string, unknown>) => {
      this.disableButton(false, this.onSuccessCallback.bind(this, response));
    };

    onFailureCallback = (failedResponse: Record<string, unknown>) => {
      const { router } = this.context;

      Instrumentation.thirdPartyError(authControlsType, failedResponse);
      const code = failedResponse?.code;

      let query = { ...router.location.query };

      // @link https://phabricator.dkandu.me/D101877#inline-888676
      if (failedResponse.error) {
        // authMode is coming from router.location.query
        query = { ...query, authType: authControlsType, errorCode: code };
      } else {
        switch (code) {
          // We need to support old cases so I'll be supporting accountLinkedToSocialAccount and
          // existingCourseraAccountNoPasswordSet until we fully deploy this
          case 'accountLinkedToSocialAccount':
          case 'existingCourseraAccountNoPasswordSet':
            query = {
              ...query,
              authMode: 'link',
              authType: authControlsType,
              serviceCode: failedResponse?.existingAccountType,
            };
            break;

          case 'unknownStatus':
            // authMode is coming from router.location.query
            query = { ...query, authType: authControlsType, errorCode: code };
            break;

          default:
            query = { ...query, authMode: 'complete', authType: authControlsType, completeMode: code };
            break;
        }
      }

      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query,
      });
    };

    handleThirdPartyAuthFailure = (failedResponse: Record<string, unknown>) => {
      this.disableButton(false, this.onFailureCallback.bind(this, failedResponse));
    };

    connectThirdPartyAuth = () => {
      thirdPartyAuth
        .connect(authControlsType)
        .then((response) => {
          this.handleThirdPartyAuthSuccess(response as Record<string, unknown>);
        })
        .catch((failedResponse) => {
          this.handleThirdPartyAuthFailure(failedResponse as Record<string, unknown>);
        });
    };

    onClickHandler = () => {
      this.disableButton(true, this.connectThirdPartyAuth);
    };

    render() {
      const { showRedesign } = this.props;
      const { disabled } = this.state;
      return (
        <WrappedComponent
          onClick={this.onClickHandler}
          onSuccess={this.onClickHandler}
          disabled={disabled}
          showRedesign={showRedesign}
        />
      );
    }
  }

  return SocialAuthControlsHOC;
};

export default withSocialAuthControls;
